export interface QuickBookingPreset {
  apartmentNumber: string;
  checkIn: string;
  checkOut: string;
}

const dayMs = 86_400_000;

export const addCalendarDays = (date: Date, days: number) => new Date(date.getTime() + days * dayMs);

export const calendarDateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export const isPastCalendarDay = (date: Date, today = new Date()) =>
  calendarDateKey(date) < calendarDateKey(today);

export const startOfCalendarMonth = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);

export const startOfCalendarWeek = (date: Date) => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return addCalendarDays(start, -((start.getDay() + 6) % 7));
};

export const calendarMonthLabel = (date: Date) =>
  date.toLocaleDateString("ru-RU", { month: "long", year: "numeric" });

/**
 * Returns a complete Monday-first month grid. Six fixed weeks keep the
 * calendar height stable while navigating between months.
 */
export function calendarMonthGrid(date: Date): Date[] {
  const monthStart = startOfCalendarMonth(date);
  const mondayOffset = (monthStart.getDay() + 6) % 7;
  const gridStart = addCalendarDays(monthStart, -mondayOffset);
  return Array.from({ length: 42 }, (_, index) => addCalendarDays(gridStart, index));
}

export function calendarBookingTouchesDay(
  booking: { checkInAt: string; checkOutAt: string; status: string },
  day: Date,
) {
  if (booking.status === "cancelled") return false;
  const dayKey = calendarDateKey(day);
  const checkInKey = calendarDateKey(new Date(booking.checkInAt));
  const checkOutKey = calendarDateKey(new Date(booking.checkOutAt));
  return dayKey >= checkInKey && dayKey <= checkOutKey;
}

export function calendarBookingOverdueState(
  booking: { checkInAt: string; checkOutAt: string; status: string },
  today = new Date(),
): "arrival" | "departure" | null {
  const todayKey = calendarDateKey(today);
  if (["confirmed", "pending_payment"].includes(booking.status)
    && calendarDateKey(new Date(booking.checkInAt)) < todayKey) return "arrival";
  if (booking.status === "checked_in"
    && calendarDateKey(new Date(booking.checkOutAt)) < todayKey) return "departure";
  return null;
}

export type CalendarBookingDayTone = "green" | "blue" | "orange" | "purple" | "pink" | "red" | "gray";

export interface CalendarBookingDayPresentation {
  label: string;
  shortLabel: string;
  tone: CalendarBookingDayTone;
}

/**
 * Booking status describes the record right now. A calendar cell describes a
 * particular day, so future dates must use planned language and past dates
 * must read as history rather than claiming that the guest lives there now.
 */
export function calendarBookingDayPresentation(
  booking: { checkInAt: string; checkOutAt: string; status: string; paid?: number; total?: number },
  day: Date,
  today = new Date(),
): CalendarBookingDayPresentation {
  const dayKey = calendarDateKey(day);
  const todayKey = calendarDateKey(today);
  const checkInKey = calendarDateKey(new Date(booking.checkInAt));
  const checkOutKey = calendarDateKey(new Date(booking.checkOutAt));
  const isArrival = dayKey === checkInKey;
  const isDeparture = dayKey === checkOutKey;

  if (dayKey > todayKey) {
    if (booking.status === "draft") return { label: "Предварительная бронь", shortLabel: "черновик", tone: "purple" };
    if (booking.status === "cancelled") return { label: "Бронь отменена", shortLabel: "отмена", tone: "gray" };
    if (booking.status === "no_show") return { label: "Гость не заехал", shortLabel: "не заехал", tone: "pink" };
    return {
      label: isArrival ? "Заезд запланирован" : isDeparture ? "Выезд запланирован" : "Забронировано",
      shortLabel: isArrival ? "заезд" : isDeparture ? "выезд" : "бронь",
      tone: booking.status === "checked_out" ? "gray" : "blue",
    };
  }

  if (dayKey < todayKey) {
    if (["confirmed", "pending_payment"].includes(booking.status) && checkInKey < todayKey) return {
      label: isArrival ? "Заезд не оформлен" : "Бронь просрочена",
      shortLabel: "просрочено",
      tone: "red",
    };
    if (booking.status === "checked_in" && checkOutKey < todayKey) return {
      label: isDeparture ? "Выезд просрочен" : "Просрочено проживание",
      shortLabel: "просрочено",
      tone: "red",
    };
    if (booking.status === "no_show") return { label: "Гость не заехал", shortLabel: "не заехал", tone: "pink" };
    if (booking.status === "cancelled") return { label: "Бронь отменена", shortLabel: "отмена", tone: "gray" };
    return {
      label: isArrival ? "Заехал" : isDeparture ? "Выехал" : "Проживал",
      shortLabel: isArrival ? "заезд" : isDeparture ? "выезд" : "история",
      tone: "gray",
    };
  }

  if (booking.status === "checked_in" && booking.paid !== undefined && booking.total !== undefined && booking.paid < booking.total) return {
    label: isDeparture ? "Выезд сегодня · есть долг" : "Проживает · есть долг",
    shortLabel: "долг",
    tone: "orange",
  };
  if (booking.status === "checked_in") return {
    label: isArrival ? "Заселён сегодня" : isDeparture ? "Выезд сегодня" : "Проживает сейчас",
    shortLabel: isArrival ? "заезд" : isDeparture ? "выезд" : "сейчас",
    tone: "green",
  };
  if (booking.status === "checked_out") return { label: "Выехал сегодня", shortLabel: "выехал", tone: "gray" };
  if (booking.status === "draft") return { label: "Предварительная бронь", shortLabel: "черновик", tone: "purple" };
  if (booking.status === "no_show") return { label: "Гость не заехал", shortLabel: "не заехал", tone: "pink" };
  if (booking.status === "cancelled") return { label: "Бронь отменена", shortLabel: "отмена", tone: "gray" };
  return {
    label: isArrival ? "Заезд сегодня" : isDeparture ? "Выезд запланирован" : "Подтверждённая бронь",
    shortLabel: isArrival ? "заезд" : isDeparture ? "выезд" : "бронь",
    tone: "blue",
  };
}

export function calendarStayOccupiesDay(
  stay: { checkInAt: string; checkOutAt: string; status: string },
  day: Date,
  today = new Date(),
) {
  if (["cancelled", "no_show"].includes(stay.status)) return false;
  const checkInDay = calendarDateKey(new Date(stay.checkInAt));
  const checkOutDay = calendarDateKey(new Date(stay.checkOutAt));
  const dayKey = calendarDateKey(day);
  if (dayKey < checkInDay) return false;
  if (stay.status === "checked_in" && checkOutDay <= calendarDateKey(today)) {
    return dayKey <= calendarDateKey(today);
  }
  const exclusiveEnd = checkOutDay > checkInDay
    ? checkOutDay
    : calendarDateKey(addCalendarDays(new Date(stay.checkInAt), 1));
  return dayKey < exclusiveEnd;
}

export function quickBookingPreset(apartmentNumber: string, start: Date, end: Date): QuickBookingPreset {
  const first = start <= end ? start : end;
  const last = start <= end ? end : start;
  return {
    apartmentNumber,
    checkIn: calendarDateKey(first),
    checkOut: calendarDateKey(addCalendarDays(last, 1)),
  };
}
